import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

// Generic paginated data table backed by a list endpoint.
export default function DataTable({
  endpoint, columns, testId = "data-table", searchable = true,
  extraParams = {}, actions, refreshKey = 0, emptyText = "No records found",
}) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const paramsKey = JSON.stringify(extraParams);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: { page, page_size: 10, search: debounced || undefined, ...extraParams },
      });
      setData(Array.isArray(data) ? { items: data, total: data.length, page: 1, total_pages: 1 } : data);
    } catch (e) {
      setData({ items: [], total: 0, page: 1, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [endpoint, page, debounced, paramsKey, refreshKey]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debounced, paramsKey]);

  return (
    <div data-testid={testId} className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg shadow-sm overflow-hidden">
      {searchable && (
        <div className="p-3 border-b border-slate-100 dark:border-border">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid={`${testId}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9 h-10"
            />
          </div>
        </div>
      )}
      <div className="w-full overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-secondary">
              {columns.map((c) => (
                <th key={c.key} className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-muted-foreground py-3 px-4 whitespace-nowrap">
                  {c.label}
                </th>
              ))}
              {actions && <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="py-12 text-center text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading...
              </td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="py-12 text-center text-slate-400">{emptyText}</td></tr>
            ) : (
              data.items.map((row, i) => (
                <tr key={row.id || i} data-testid={`${testId}-row-${i}`}
                    className="border-t border-slate-100 dark:border-border hover:bg-slate-50 dark:hover:bg-secondary/50 transition-colors">
                  {columns.map((c) => (
                    <td key={c.key} className={`py-3 px-4 text-sm text-slate-700 dark:text-foreground whitespace-nowrap ${c.mono ? "font-mono text-xs" : ""}`}>
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                  {actions && <td className="py-3 px-4 text-right">{actions(row, load)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between p-3 border-t border-slate-100 dark:border-border text-sm text-slate-500">
        <span data-testid={`${testId}-total`}>{data.total} record(s)</span>
        <div className="flex items-center gap-2">
          <button data-testid={`${testId}-prev`} disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="p-2 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50 dark:border-border dark:hover:bg-secondary">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span>Page {data.page} / {data.total_pages}</span>
          <button data-testid={`${testId}-next`} disabled={page >= data.total_pages}
            onClick={() => setPage((p) => p + 1)}
            className="p-2 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50 dark:border-border dark:hover:bg-secondary">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
